import type { ComponentType, ReactElement } from 'react';
import type { AuthOtpRequestTrigger } from '@shared/utils/analytics/events';
import { PHONE_SMS_OTP_AUTH_INTERACTION_IDS } from './interactions';
import {
  isPhoneSmsOtpAuthScreen,
  PhoneSmsOtpAuthScreenNames,
} from './screenMapping';
import type {
  OTPVerificationScreenProps,
  PhoneCountryCode,
  PhoneLoginScreenProps,
} from '../universal-login/types/descope';

type DescopeNext = (
  interactionId: string,
  payload?: Record<string, unknown>
) => Promise<unknown>;

type DescopeRecord = Record<string, unknown>;

type DescopeFlowState = DescopeRecord & {
  screenName?: string;
  error?: { text?: string };
  next?: DescopeNext;
};

export interface PhoneSmsOtpAuthRouteScreenProps {
  screenName?: string;
  state: DescopeFlowState;
  formWithoutSmsAuthFlow: DescopeRecord;
  smsAuthForm: DescopeRecord;
  smsScreenContext: DescopeRecord;
  smsAuthAllowedCountries: PhoneCountryCode[];
  onFormUpdate: (data: DescopeRecord) => void;
  onPhoneUpdate: (phone: string | undefined) => void;
  onPhoneLoginBack?: () => boolean | Promise<boolean>;
  resetError: () => void;
  consumeInitialOtpChallengeRequestTrigger?: () =>
    AuthOtpRequestTrigger | undefined;
  PhoneLoginScreen: ComponentType<PhoneLoginScreenProps>;
  OtpVerificationScreen: ComponentType<OTPVerificationScreenProps>;
}

export const routePhoneSmsOtpAuthScreen = ({
  screenName,
  state,
  formWithoutSmsAuthFlow,
  smsAuthForm,
  smsScreenContext,
  smsAuthAllowedCountries,
  onFormUpdate,
  onPhoneUpdate,
  onPhoneLoginBack,
  resetError,
  consumeInitialOtpChallengeRequestTrigger,
  PhoneLoginScreen,
  OtpVerificationScreen,
}: PhoneSmsOtpAuthRouteScreenProps): ReactElement | null => {
  if (!screenName || !isPhoneSmsOtpAuthScreen(screenName)) {
    return null;
  }

  if (screenName === PhoneSmsOtpAuthScreenNames.PHONE_LOGIN_SCREEN) {
    return (
      <PhoneLoginScreen
        authFlow="login"
        allowedCountries={smsAuthAllowedCountries}
        defaultCountry="US"
        onClickContinue={async (phone: string) => {
          const nextForm = { ...smsAuthForm, phone };
          onPhoneUpdate(phone);
          onFormUpdate(formWithoutSmsAuthFlow);
          if (state.next) {
            await state.next(
              PHONE_SMS_OTP_AUTH_INTERACTION_IDS.SUBMIT_PHONE,
              nextForm
            );
          }
        }}
        onBackButtonClick={async () => {
          onPhoneUpdate(undefined);
          if (await onPhoneLoginBack?.()) {
            return;
          }

          onFormUpdate(formWithoutSmsAuthFlow);
          if (state.next) {
            await state.next(
              PHONE_SMS_OTP_AUTH_INTERACTION_IDS.CHOOSE_ANOTHER_AUTH_METHOD,
              formWithoutSmsAuthFlow
            );
          }
        }}
        errorText={state?.error?.text}
        context={smsScreenContext}
        resetError={resetError}
      />
    );
  }

  return (
    <OtpVerificationScreen
      authFlow="login"
      otpChannel="sms"
      consumeInitialOtpChallengeRequestTrigger={
        consumeInitialOtpChallengeRequestTrigger
      }
      onFormUpdate={onFormUpdate}
      onOTPVerificationClick={async (code: string) => {
        if (state.next) {
          await state.next(PHONE_SMS_OTP_AUTH_INTERACTION_IDS.VERIFY_CODE, {
            ...smsAuthForm,
            code,
          });
        }
      }}
      onClickResend={async () => {
        if (state.next) {
          await state.next(
            PHONE_SMS_OTP_AUTH_INTERACTION_IDS.RESEND,
            smsAuthForm
          );
        }
      }}
      onClickUseDifferentNumber={async () => {
        if (state.next) {
          await state.next(
            PHONE_SMS_OTP_AUTH_INTERACTION_IDS.USE_DIFFERENT_NUMBER,
            smsAuthForm
          );
        }
      }}
      onClickChooseAnotherAuthMethod={async () => {
        if (state.next) {
          await state.next(
            PHONE_SMS_OTP_AUTH_INTERACTION_IDS.CHOOSE_ANOTHER_AUTH_METHOD,
            smsAuthForm
          );
        }
      }}
      errorText={state?.error?.text}
      context={smsScreenContext}
      resetError={resetError}
    />
  );
};
